import { useParams } from "@tanstack/react-router";
import { Lock, MessageSquare } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ReportSheet } from "@/components/report/report-sheet";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  getStoredShareSession,
  useAddShareComment,
  useShareManifest,
  useShareTriageItems,
  useVerifySharePassword,
} from "@/hooks/use-share";
import type { TriageItemRow } from "@/hooks/use-triage";

const COMMENTER_NAME_KEY = "scope-poc-commenter-name";

export function SharePage() {
  const { token } = useParams({ from: "/r/$token" });
  const manifest = useShareManifest(token);
  const [verified, setVerified] = useState<boolean>(() => Boolean(getStoredShareSession(token)));

  const passwordGateOpen = Boolean(manifest.data?.passwordProtected && !verified);
  const triage = useShareTriageItems(token, Boolean(manifest.data) && !passwordGateOpen);

  const [commentItem, setCommentItem] = useState<TriageItemRow | null>(null);

  // Re-lock if the API tells us the session expired (401).
  useEffect(() => {
    if (triage.error instanceof Error && triage.error.message === "password_required") {
      setVerified(false);
    }
  }, [triage.error]);

  if (manifest.isLoading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-7xl space-y-3">
          <Skeleton className="h-8 w-1/3" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  if (manifest.isError || !manifest.data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-8">
        <div className="max-w-md w-full">
          <ErrorState
            message="This share link is invalid or has expired."
            onRetry={() => manifest.refetch()}
          />
        </div>
      </div>
    );
  }

  const { solution, allowComments, expiresAt, passwordProtected } = manifest.data;

  if (passwordProtected && !verified) {
    return (
      <PasswordGate
        token={token}
        solutionName={solution.name}
        clientName={solution.clientName}
        onVerified={() => setVerified(true)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-[var(--color-border)] bg-card">
        <div className="mx-auto max-w-7xl px-6 py-4 flex items-center gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
              Read-only · expires {new Date(expiresAt).toLocaleDateString()}
            </div>
            <h1 className="font-display text-xl font-semibold">
              {solution.clientName} · {solution.name}
            </h1>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl p-6 space-y-4">
        <ReportSheet
          solutionName={solution.name}
          clientName={solution.clientName}
          reportTitle="Scope Brief"
          items={triage.data?.data}
          isLoading={triage.isLoading}
          hideShareButton
          rowAction={
            allowComments
              ? (row) => (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCommentItem(row)}
                    title="Add comment"
                  >
                    <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
                    {row.notes.length > 0 ? row.notes.length : "Comment"}
                  </Button>
                )
              : undefined
          }
        />
        {!allowComments && (
          <p className="text-xs text-muted-foreground text-center">
            Comments are disabled on this share link.
          </p>
        )}
      </main>

      {allowComments && (
        <CommentSheet token={token} item={commentItem} onClose={() => setCommentItem(null)} />
      )}
    </div>
  );
}

function CommentSheet({
  token,
  item,
  onClose,
}: {
  token: string;
  item: TriageItemRow | null;
  onClose: () => void;
}) {
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const add = useAddShareComment(token);

  // Hydrate the commenter name once per browser
  useEffect(() => {
    if (typeof window === "undefined") return;
    const saved = window.localStorage.getItem(COMMENTER_NAME_KEY);
    if (saved) setName(saved);
  }, []);

  // Reset text when switching items
  useEffect(() => {
    setText("");
  }, []);

  function handleSubmit() {
    if (!item) return;
    if (!name.trim() || !text.trim()) {
      toast.error("Name and comment are both required");
      return;
    }
    add.mutate(
      { itemId: item.id, authorName: name.trim(), text: text.trim() },
      {
        onSuccess: () => {
          if (typeof window !== "undefined") {
            window.localStorage.setItem(COMMENTER_NAME_KEY, name.trim());
          }
          toast.success("Comment posted");
          setText("");
        },
      },
    );
  }

  return (
    <Sheet open={Boolean(item)} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-[520px] overflow-y-auto">
        {item && (
          <>
            <SheetHeader className="border-b border-[var(--color-border)]">
              <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                {item.externalKey ?? item.id.slice(0, 8)} ·{" "}
                {item.source.externalKey ?? item.source.name}
              </div>
              <SheetTitle className="font-display text-base leading-tight mt-1">
                {item.extracted}
              </SheetTitle>
              {item.quote && (
                <SheetDescription className="mt-2 italic text-foreground/70">
                  "{item.quote}"
                </SheetDescription>
              )}
            </SheetHeader>

            <div className="p-6 space-y-6">
              <section>
                <h3 className="font-display text-sm font-semibold mb-2">
                  Comments
                  {item.notes.length > 0 && (
                    <span className="ml-2 text-xs text-muted-foreground font-normal">
                      ({item.notes.length})
                    </span>
                  )}
                </h3>
                {item.notes.length > 0 ? (
                  <ul className="space-y-2">
                    {item.notes.map((n) => (
                      <li
                        key={n.id}
                        className="rounded-md border border-[var(--color-border)] p-3 bg-card"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium">{n.authorName ?? "Anonymous"}</span>
                          <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                            {new Date(n.createdAt).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </span>
                        </div>
                        <p className="text-sm text-foreground/85 whitespace-pre-wrap">{n.text}</p>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-muted-foreground">No comments yet.</p>
                )}
              </section>

              <section className="space-y-2">
                <h3 className="font-display text-sm font-semibold">Add a comment</h3>
                <div>
                  <label
                    htmlFor="comment-name"
                    className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold mb-1.5 block"
                  >
                    Your name
                  </label>
                  <Input
                    id="comment-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Ken Eckland"
                    className="h-9 text-sm"
                  />
                </div>
                <div>
                  <label
                    htmlFor="comment-text"
                    className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold mb-1.5 block"
                  >
                    Comment
                  </label>
                  <Textarea
                    id="comment-text"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Share your thoughts on this item…"
                    rows={4}
                    className="text-sm"
                  />
                </div>
                <div className="flex justify-end pt-1">
                  <Button
                    size="sm"
                    onClick={handleSubmit}
                    disabled={!name.trim() || !text.trim() || add.isPending}
                  >
                    {add.isPending ? "Posting…" : "Post comment"}
                  </Button>
                </div>
              </section>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function PasswordGate({
  token,
  solutionName,
  clientName,
  onVerified,
}: {
  token: string;
  solutionName: string;
  clientName: string;
  onVerified: () => void;
}) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const verify = useVerifySharePassword(token);

  function submit() {
    if (!password) {
      setError("Enter a password");
      return;
    }
    setError(null);
    verify.mutate(password, {
      onSuccess: () => {
        onVerified();
      },
      onError: (err: Error) => {
        if (err.message === "invalid_password") setError("Incorrect password");
        else setError("Couldn't verify — try again");
      },
    });
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-8">
      <div className="w-full max-w-sm rounded-xl border border-[var(--color-border)] bg-card p-6 shadow-sm space-y-4">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-info-bg text-info">
            <Lock className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
              Password required
            </div>
            <div className="font-display text-base font-semibold truncate">
              {clientName} · {solutionName}
            </div>
          </div>
        </div>

        <p className="text-sm text-muted-foreground">
          This share link is password-protected. Enter the password your sender provided.
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          className="space-y-2"
        >
          <Input
            type="password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="text-sm"
            disabled={verify.isPending}
          />
          {error && <p className="text-xs text-destructive">{error}</p>}
          <Button type="submit" className="w-full" disabled={verify.isPending || !password}>
            {verify.isPending ? "Verifying…" : "Unlock"}
          </Button>
        </form>
      </div>
    </div>
  );
}
